



















































































import {
  defineComponent,
  PropType,
  ref,
  computed,
  onMounted,
  watch,
  useContext
} from '@nuxtjs/composition-api';
import { useCustomOptions } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomOptions';
import type { Product } from '@wemade-vsf/catalog/lib/runtime/product/types';
import { useCart, useUser } from '@wemade-vsf/customer';
import { useWishlist } from '@wemade-vsf/wishlist';
import { useWmAttributes } from '@wemade-vsf/catalog/lib/runtime/product/composables/useWmAttributes';
import { usePriceRequest } from '@wemade-vsf/catalog/lib/runtime/product/composables/usePriceRequest';
import { useCustomerPricing } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing';
import { getPrice } from '@wemade-vsf/catalog/lib/runtime/product/getters/productGetters';
import {
  SfGallery,
  SfButton,
  SfAlert
} from '@storefront-ui/vue';
import { ValidationObserver } from 'vee-validate';
import ProductDetailsHeader from 'components/catalog/Product/Details/ProductDetailsHeader.vue';
import ProductGallery from 'components/catalog/Product/Details/ProductGallery.vue';
import ProductPrice from 'components/catalog/Product/ProductPrice.vue';
import ProductActions from 'components/catalog/Product/Details/ProductActions.vue'
import ProductUsp from 'components/catalog/Product/Details/ProductUsp.vue';
import SkeletonLoader from 'components/theme/SkeletonLoader.vue';
import type { Price } from '@wemade-vsf/catalog/lib/runtime/types';

export default defineComponent({
  name: 'ProductDetails',
  transition: 'fade',
  props: {
    product: {
      type: [Object, null] as PropType<Product>,
      default: null
    },
    isFetching: {
      type: Boolean,
      default: true
    }
  },
  components: {
    ValidationObserver,
    ProductDetailsHeader,
    ProductGallery,
    SkeletonLoader,
    SfGallery,
    ProductPrice,
    SfAlert,
    SfButton,
    ProductActions,
    ProductUsp,
    ProductGalleryModal: () => import('components/catalog/Product/Details/ProductGalleryModal.vue'),
    PriceRequestButton: () => import('components/catalog/Product/PriceRequestButton.vue')
  },
  setup (props) {
    const qty = ref(1)
    const { app: { i18n } } = useContext();
    const {
      addItem, error: cartError, loading: isCartLoading, canAddToCart,
    } = useCart();
    const { isInWishlist, addOrRemoveItem, enabled: isWishlistEnabled } = useWishlist();
    const { productAttributes, deliveryTime } = useWmAttributes();
    const { getPriceForProduct, displayPricesIncludingTax, showCartButtonForProduct, showPriceForProduct, getNetPrice } = useCustomerPricing();

    const { priceForOptions, customOptions } = useCustomOptions();
    const productPrice = ref(getPriceForProduct(props.product));
    const productPriceRegular = computed(() => productPrice.value?.regular ?? null);
    const productSpecialPrice = computed(() => productPrice.value?.special ?? null);
    const hasPriceRange = computed(() => props.product?.__typename && ['ConfigurableProduct', 'BundleProduct', 'GroupedProduct'].includes(props.product.__typename))

    const recommendedNetPrice = computed<Price | null>(() => {
      if (!props.product || !productPrice.value) return null;
      const price = getPrice(props.product, true);
      if (!price) return null;
      const priceRaw = getNetPrice(price, props.product.applied_tax_rate);
      return priceRaw;
    });

    const { isQuoteProduct, showPriceOnQuoteProduct } = usePriceRequest();

    const showAddToCart = ref<boolean>(false);
    
    const addToCartError = computed(() => cartError.value?.addItem?.message);
    const galleryLoaded = ref(false)
    const showGalleryModal = ref(false)
    const currentGalleryIndex = ref(1)

    const openGalleryModal = (index) => {
      galleryLoaded.value = true
      showGalleryModal.value = true
      currentGalleryIndex.value = index
    }

    const { isAuthenticated, user, load: loadUser } = useUser();
    function updateClientValues() {
      if (props.product) {
        productPrice.value = getPriceForProduct(props.product);
        showAddToCart.value = showCartButtonForProduct(props.product);
      }
    }

    onMounted(async () => {
      if (isAuthenticated.value && !user.value) {
        await loadUser();
      }
      updateClientValues()
    })

    watch(() => props.product, (newVal) => {
      updateClientValues()
    }, { deep: true, immediate: true });
    watch(() => isAuthenticated.value, () => {
      updateClientValues()
    }, { deep: true, immediate: true })

    return {
      addItem,
      addItemToWishlist: addOrRemoveItem,
      hasPriceRange,
      arePricesIncludingTax: displayPricesIncludingTax,
      addToCartError,
      cartError,
      canAddToCart,
      currentGalleryIndex,
      galleryLoaded,
      isCartLoading,
      isWishlistEnabled,
      isInWishlist: computed(() => isInWishlist({ product: props.product })),
      openGalleryModal,
      productPrice: productPriceRegular,
      productSpecialPrice,
      priceForOptions,
      customOptions,
      showGalleryModal,
      isQuoteProduct: computed(() => isQuoteProduct(props.product, productAttributes.value)),
      showPriceOnQuoteProduct,
      showAddToCart,
      showPrice: computed(() => showPriceForProduct(props.product)),
      qty,
      deliveryTime,
      recommendedNetPrice
    }
  }
})
